import { Component, AfterViewInit } from '@angular/core';

import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';

import { ScrAuthenticationService, SignInStateEvent } from '../../services/authentication.service';
import { NGXLogger } from 'ngx-logger';
import { ScrEnvironment } from '../../../environments/environment';
import { Router } from '@angular/router';


/**
 * This component supports an inactivity timeout capability.
 */

@Component({
  selector: 'app-inactivity',
  templateUrl: './inactivity.component.html',
  styleUrls: ['./inactivity.component.scss']
})
export class ScrInactivityComponent implements AfterViewInit {

  /**
   * When inactive, how long has it been.  This is displayed to the user.
   */
  public inactiveMinutes?: number;

  /**
   * Formatted amount of time left before being forced out.
   */
  public timeLeft?: string;

  /**
   * True if the idle dialog is to be visible.
   */
  public displayIdleDialog = false;

  /**
   * If user timed out but it has already passed.
   */
  public timeoutInFuture = false;

  /**
   * Text to display for when user will time out.
   */
  public timeoutText = '';

  /**
   * Name of product that appears in the messages.
   */
  public productName?: string;

  /**
   * Number of seconds for to allow before inactivity times out the user.
   */
  public inactivityTimeout: number;

  /**
   * Number of seconds prior to timeout that the warning dialog should appear.
   */
  public inactivityWarningTimeout: number;

  private lastActivityTime: number;


  /**
   * Constructor with parameters injected.
   */
  public constructor(
    private authService: ScrAuthenticationService,
    environment: ScrEnvironment,
    private router: Router,
    private logger: NGXLogger,
    protected idle: Idle) {

      this.inactivityTimeout = environment.timeout;
      this.inactivityWarningTimeout = environment.timeoutWarning;
      this.productName = environment.productName;
      this.lastActivityTime = new Date().getTime();
  }

  /**
   * Initialize the idle processing and install listeners
   * for session timeout and login/logout events.
   */
  ngAfterViewInit() {

    // use defaults from security config.
    this.logger.trace('setting inactivity timeout to ' + this.inactivityTimeout + ' seconds');

    this.idle.setIdle(this.inactivityTimeout);
    this.idle.setTimeout(this.inactivityWarningTimeout);

    this.idle.onInterrupt.subscribe(() => this.gotUserActivity());
    this.idle.onIdleEnd.subscribe(() => this.noLongerIdle());
    this.idle.onTimeout.subscribe(() => this.idleTimeout());
    this.idle.onIdleStart.subscribe(() => this.goneIdle());
    this.idle.onTimeoutWarning.subscribe((countdown: number) => this.idleCountdown(countdown));
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.setUserState(this.authService.isUserSignedIn());

    // Subscribe to the auth service for updates to the user.
    this.authService.signInState.subscribe((sse:SignInStateEvent) => {
      this.setUserState(sse.signedIn || false);
    });


    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === 'visible') {
        const now = new Date().getTime();
        const diff = now - this.lastActivityTime;
        const seconds = diff / 1000;
        if (seconds > this.inactivityTimeout) {
          this.logger.trace('page visible but timeout is ' + seconds + ' vs inactivity timeout of ' + this.inactivityTimeout);
          if (this.idle.isRunning()) {
            this.logMeOut();
          }
          // make sure we don't pop back and leave the dialog up.
          this.displayIdleDialog = false;
        }
        else {
          this.idle.interrupt(true);
        }
      }
    }, false);
  }

  /**
   * Callback from idle processing.  Nothing to do.
   */
  gotUserActivity() {
    // this.log.debug('user activity');
    this.lastActivityTime = new Date().getTime();
  }

  /**
   * Format the number of seconds left until forced logout as mm:ss.
   * @param secs - number of seconds left before inactivity forces a logout.
   */
  setTimeLeft(secs: number) {
    const minutes = Math.floor(secs / 60);
    const seconds = secs - minutes*60;
    this.timeLeft = `${minutes}`.padStart(2,'0') + ':' + `${seconds}`.padStart(2,'0');
  }

  /**
   * User is idle.   We show the dialog and force them to continue their session
   * by pressing the button.
   */
  public goneIdle() {
    this.logger.debug('user has gone idle at ' + new Date());
    this.idle.setInterrupts([]);
    this.setTimeLeft(this.inactivityWarningTimeout);
    this.displayIdleDialog = true;
    this.inactiveMinutes = Math.round(this.inactivityWarningTimeout / 60);
  }

  onDialogHide() {
    this.logger.trace('user closed dialog.');
    this.reset();
  }

  /**
   * User pressed the continue working button so we need to reset
   * the inactivity tracking and hide the dialog.
   */
  continueWorking() {
    this.displayIdleDialog = false;
    this.reset();
  }

  /**
   * User chose to log out (or was forced out).  Hide the dialog
   * if shown and send them to the login page with the logout
   * option set to true.
   */
  logMeOut() {
    this.displayIdleDialog = false;
    this.router.navigate(['/sec/inactivitylogout'], {state: {saveState: true, unattended: true}});
  }

  /**
   * User didn't respond to the dialog so we force a logout.
   */
  public idleTimeout() {
    this.logger.debug('user did not response - log them out - ' + new Date());
    this.logMeOut();
  }

  /**
   * User was idle but moved the mouse or something to wake up the app.
   */
  public noLongerIdle() {
    this.logger.trace('no longer idle at ' + new Date());
    // nothing to do.  Since we display the dialog and ask that
    // the user press a button to continue, we moving the mouse
    // should not remove the dialog.
  }

  /**
   * The user is idle and we're counting down to their logout.
   * @param seconds - number of seconds left until logout.
   */
  public idleCountdown(seconds: number) {
    this.setTimeLeft(seconds);
  }

  /**
   * Reset the idle tracking either initially or after user
   * presses continue working.
   */
  public reset() {
    this.logger.trace('inactivity timeout reset at ' + new Date());
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    if (this.idle.isRunning()) {
      this.idle.stop();
    }
    this.idle.watch();
  }

  /**
   * Upon login or when the application starts, this is called with the
   * sign-in state.
   */
  public setUserState(state: boolean) {
    if (state) {
      this.logger.trace('setUserState: user is signed in, call reset');
      this.reset();
    } else {
      if (this.idle) {
        this.logger.trace('setUserState: user not signed in so stop the idle processing');
        this.idle.stop();
      }
    }
  }
}